import React, {
  createContext,
  Dispatch,
  ReactNode,
  Reducer,
  ReducerAction,
  ReducerState,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { createSlice, AnyAction } from '@reduxjs/toolkit';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import _, { cloneDeep, sum } from 'lodash';
import { Loader } from '../../components/Loader';
import { useStoreContext } from '../../factorys/useStoreContext';
import { IdentityContext } from '../App/identity.context';
import {
  AddArtworkTagsDocument,
  DeleteArtworkTagsDocument,
  GetTeacherEvaluationPageDocument,
  GetTeacherEvaluationPageQuery,
  GetUserAssignmentGroupsDocument,
  Submission,
  SubmissionStatus,
  TeacherEvaluationPageSubmissionFragmentDoc,
  UserIdentity,
} from '../../services/graphql/types/graphql';
import { useLocalStorage } from 'react-use';
import { getFragmentData } from '../../services/graphql/types';

export interface AssignmentArtwork {
  assignmentId: string;
  [key: string]: any;
}

const initialState = {
  submissionFilter: 'all' as 'all' | 'pending' | 'star',
  showTranscriptPreview: false,
  activeStudentIndex: 0,
  activeArtworkIndex: 0,
  showPendingAction: false,
  activeGradeFilter: 'all' as 'all' | 'better' | 'normal' | 'weaker',
  sortBy: 'studentNumber' as 'studentNumber' | 'name' | 'grade',
  assignmentData: undefined as
    | undefined
    | (GetTeacherEvaluationPageQuery & {
        studentArtworks: (UserIdentity & { submissions: Submission[] })[];
      }),
};

type TeacherEvaluationPageState = typeof initialState;

const { actions, reducer } = createSlice({
  name: 'TeacherEvaluationPageState',
  initialState,
  reducers: {
    setSubmissionFilter: (state, action) => {
      if (state.submissionFilter !== action.payload) {
        state.submissionFilter = action.payload;
        state.activeStudentIndex = 0;
      }
    },
    setActiveStudentIndex: (state, action) => {
      state.activeStudentIndex = action.payload;
    },
    setAssignmentData: (state, action) => {
      state.assignmentData = action.payload;
    },
    setShowTranscriptPreview: (state, action) => {
      state.showTranscriptPreview = action.payload;
    },
    setActiveGradeFilter: (state, action) => {
      state.activeGradeFilter = action.payload;
    },
    setSortMethod: (state, action) => {
      if (state.sortBy !== action.payload) {
        state.sortBy = action.payload;
        state.activeStudentIndex = 0;
      }
    },
    setActiveArtwork: (state, action) => {
      state.activeArtworkIndex = action.payload;
    },
  },
});

const useTeacherEvaluationPageStore = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { currentIdentity } = useStoreContext(IdentityContext);

  return {
    state,
    dispatch,
  };
};

export const assignmentDataSelector = (state: TeacherEvaluationPageState) => state.assignmentData;
// export const assignmentKnowledgePointsSelector = (state: TeacherEvaluationPageState) => {
//   return state.assignmentData?.assignment.knowledgePoints ?? [];
// };

// export const studentGradesSelector = (state: TeacherEvaluationPageState) => {
//   return state.assignmentData?.assignment.studentGrades;
// };

const allStudentsSelector = (state: TeacherEvaluationPageState) =>
  state.assignmentData?.studentArtworks;

export const resolveGraderRole = (role?: string | null): 'teacher' | 'ta' =>
  role === 'ta' ? 'ta' : 'teacher';

const getAssignmentSubmission = (student: UserIdentity & { submissions?: Submission[] }) =>
  student.submissions?.[0];

const hasSubmittedWork = (submission?: Submission) =>
  submission?.artworks?.some((artwork) => (artwork.medias?.length ?? 0) > 0) ?? false;

const isStarredStudent = (student: UserIdentity & { submissions?: Submission[] }) => {
  const submission = getAssignmentSubmission(student);
  return (
    submission?.artworks?.some((artwork) =>
      artwork.tags?.some((tag) => tag.name === 'internal--star'),
    ) ?? false
  );
};

const isPendingStudent = (
  student: UserIdentity & { submissions?: Submission[] },
  role: 'teacher' | 'ta',
) => {
  const submission = getAssignmentSubmission(student);
  if (!submission || !hasSubmittedWork(submission)) {
    return false;
  }

  if (role === 'ta') {
    return submission.submissionStatus === SubmissionStatus.Submitted;
  }

  // coca 模式下教师直接批改 Submitted 作业；兼容 TA 流程中的 TAApproved；精轨流程中的 Resubmitted
  return [
    SubmissionStatus.Submitted,
    SubmissionStatus.TaApproved,
    SubmissionStatus.Resubmitted,
  ].includes(submission.submissionStatus);
};

export const filteredStudentsSelector = (
  state: TeacherEvaluationPageState,
  role?: string | null,
) => {
  const graderRole = resolveGraderRole(role);
  let students = allStudentsSelector(state) ?? [];

  if (state.submissionFilter === 'star') {
    students = students.filter(isStarredStudent);
  } else if (state.submissionFilter === 'pending') {
    students = students.filter((student) => isPendingStudent(student, graderRole));
  }

  students = students.toSorted((a, b) => {
    switch (state.sortBy) {
      case 'studentNumber':
        return a.studentNumber.localeCompare(b.studentNumber);
      case 'name':
        return a.user.name.localeCompare(b.user.name);
      case 'grade': {
        const gradeA = a.submissions?.[0]?.calculatedGrade ?? -Infinity;
        const gradeB = b.submissions?.[0]?.calculatedGrade ?? -Infinity;
        return gradeB - gradeA;
      }
      default:
        return a.studentNumber.localeCompare(b.studentNumber);
    }
  });

  return students;
};

export const activeStudentSelector = (state: TeacherEvaluationPageState, role: string) => {
  return filteredStudentsSelector(state, role)[state.activeStudentIndex];
};

export const activeSubmissionSelector = (state: TeacherEvaluationPageState, role: string) => {
  return activeStudentSelector(state, role)?.submissions?.[state.activeArtworkIndex];
};

export const TeacherEvaluationPageContext = createContext({
  state: initialState,
  currentGroupId: undefined as string | undefined,
  setCurrentGroupId: undefined as undefined | ReturnType<typeof useLocalStorage<string>>[1],
  dispatch: (() => {}) as React.Dispatch<AnyAction>,
  toggleStarStatus: (async () => {}) as (
    submissionId: string,
    artworkId: string,
    current: boolean,
  ) => Promise<void>,
});

export const TeacherEvaluationPageProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const apollo = useApolloClient();

  const { currentClassId, currentSchoolId, currentIdentity } = useStoreContext(IdentityContext);
  const { data: userAssignmentGroupsData } = useQuery(GetUserAssignmentGroupsDocument);
  const { state, dispatch } = useTeacherEvaluationPageStore();
  const assignmentData = useMemo(() => assignmentDataSelector(state), [state]);

  const lsCurrentGroupStorage = useLocalStorage('CurrentGroupId', '');
  const [lsCurrentGroupId, setLsCurrentGroupId] = lsCurrentGroupStorage;

  const isGroupAssignment = assignmentData?.assignment.isGroupAssignment;

  useEffect(() => {
    if (
      userAssignmentGroupsData?.userAssignmentGroups.filter(
        (uag) =>
          uag.assignmentGroup.schoolId === currentSchoolId &&
          uag.assignmentGroup.assignmentGroupId === lsCurrentGroupId,
      ).length === 0
    ) {
      setLsCurrentGroupId(
        userAssignmentGroupsData?.userAssignmentGroups.filter(
          (uag) => uag.assignmentGroup.schoolId === currentSchoolId,
        )?.[0]?.assignmentGroup.assignmentGroupId,
      );
    }
  }, [currentSchoolId, lsCurrentGroupId, setLsCurrentGroupId, userAssignmentGroupsData]);

  useEffect(() => {
    if (isGroupAssignment && !lsCurrentGroupId && userAssignmentGroupsData) {
      setLsCurrentGroupId(
        userAssignmentGroupsData.userAssignmentGroups.filter(
          (uag) => uag.assignmentGroup.schoolId === currentSchoolId,
        )?.[0]?.assignmentGroup.assignmentGroupId,
      );
    }
  }, [
    userAssignmentGroupsData,
    assignmentData,
    isGroupAssignment,
    lsCurrentGroupId,
    setLsCurrentGroupId,
    currentSchoolId,
  ]);

  useEffect(() => {
    if (assignmentData && userAssignmentGroupsData) {
      setLsCurrentGroupId(
        userAssignmentGroupsData.userAssignmentGroups.filter(
          (uag) => uag.assignmentGroup.schoolId === currentSchoolId,
        )?.[0]?.assignmentGroup.assignmentGroupId,
      );
    }
  }, [
    assignmentData,
    currentSchoolId,
    setLsCurrentGroupId,
    userAssignmentGroupsData,
  ]);

  const classId = currentClassId;
  const courseId = searchParams.get('courseId');
  const assignmentId = searchParams.get('assignmentId');
  const lessonId = searchParams.get('lessonId');

  const assignmentGroupId = lsCurrentGroupId;

  const filteredStudent = useMemo(
    () => filteredStudentsSelector(state, currentIdentity!.identity),
    [currentIdentity, state],
  );
  // useEffect(() => console.info('filteredStudent', filteredStudent), [filteredStudent]);

  // Skip query if no assignmentId - user needs to select one first
  const shouldSkipQuery = !assignmentId || !courseId;

  const { loading, data, error, updateQuery } = useQuery(GetTeacherEvaluationPageDocument, {
    variables: {
      assignmentId: assignmentId || '',
      classId,
      // assignmentGroupId: (isGroupAssignment ? assignmentGroupId : null) ?? null,
    },
    skip: shouldSkipQuery,
  });

  // useEffect(() => console.info('GetTeacherEvaluationPageDocument', data), [data]);

  const [addArtworkTagsMutation] = useMutation(AddArtworkTagsDocument);
  const [deleteArtworkTagsMutation] = useMutation(DeleteArtworkTagsDocument);
  const toggleStarStatus = async (submissionId: string, artworkId: string, current: boolean) => {
    if (!artworkId) {
      throw new Error('Artwork not found');
    }

    if (current) {
      await deleteArtworkTagsMutation({ variables: { artworkId, tags: ['internal--star'] } });
    } else {
      await addArtworkTagsMutation({ variables: { artworkId, tags: ['internal--star'] } });
    }

    await apollo.refetchQueries({
      include: [GetTeacherEvaluationPageDocument],
    });
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const submissions = (data.submissions ?? []).map((s) =>
      getFragmentData(TeacherEvaluationPageSubmissionFragmentDoc, s),
    );
    const submissionsGroupByStudent = _.groupBy(submissions, (s) => s.authors[0].userId);
    const studentArtworks = data.class.classUsers.map((s) => ({
      ...s,
      submissions: submissionsGroupByStudent[s.userId] ?? [],
    }));

    dispatch(
      actions.setAssignmentData({
        ...data,
        studentArtworks,
      }),
    );
  }, [data, dispatch]);

  // Show loading only if we're actually querying
  if (!shouldSkipQuery && (loading || !userAssignmentGroupsData)) {
    return <Loader fullscreen source="TeacherEvaluationPageProvider" />;
  }

  if (!shouldSkipQuery && assignmentData?.assignment.isGroupAssignment && !assignmentGroupId) {
    return <Loader fullscreen source="TeacherEvaluationPageProvider" />;
  }

  return (
    <TeacherEvaluationPageContext.Provider
      value={{
        state,
        currentGroupId: lsCurrentGroupId,
        setCurrentGroupId: setLsCurrentGroupId,
        dispatch,
        toggleStarStatus,
      }}
    >
      {children}
    </TeacherEvaluationPageContext.Provider>
  );
};

type ReducerType<S, A> = [ReducerState<Reducer<S, A>>, Dispatch<ReducerAction<Reducer<S, A>>>];

export const TeacherEvaluationPageActions = actions;
